import { Button, IconButton, TextField, Tooltip } from '@mui/material';
import SpinnerButton from '../../../components/SpinnerButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLogin } from '../hooks/useLogin';
import { APP_LABELS, LOGIN_LABELS } from '../../../utils/labels';

interface LoginPageProps {
  authError: string | null;
  clearAuthError: () => void;
}

export default function LoginPage({ authError, clearAuthError }: LoginPageProps) {
  const {
    email,
    setEmail,
    password,
    setPassword,
    error,
    loading,
    magicSent,
    forgotView,
    resetSent,
    signIn,
    sendMagicLink,
    sendResetEmail,
    showForgot,
    hideForgot,
  } = useLogin();

  const renderCardContent = () => {
    if (magicSent) {
      return (
        <div className="text-center py-4">
          <div className="flex justify-center mb-3">
            <CheckCircleIcon className="text-primary" />
          </div>
          <p className="font-medium mb-1">{LOGIN_LABELS.magicLinkSentTitle}</p>
          <p className="text-sm text-text-secondary">
            {LOGIN_LABELS.magicLinkSentBody} <span className="font-medium">{email}</span>
          </p>
        </div>
      );
    }

    if (forgotView) {
      if (resetSent) {
        return (
          <div className="text-center py-4">
            <div className="flex justify-center mb-3">
              <CheckCircleIcon className="text-primary" />
            </div>
            <p className="font-medium mb-1">{LOGIN_LABELS.resetSentTitle}</p>
            <p className="text-sm text-text-secondary">
              {LOGIN_LABELS.resetSentBody} <span className="font-medium">{email}</span>
            </p>
            <Button
              variant="text"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={hideForgot}
              className="mt-4"
            >
              {LOGIN_LABELS.backToLogin}
            </Button>
          </div>
        );
      }

      return (
        <div className="flex flex-col gap-5">
          <div>
            <p className="font-medium mb-1">{LOGIN_LABELS.forgotPasswordTitle}</p>
            <p className="text-sm text-text-secondary">{LOGIN_LABELS.forgotPasswordBody}</p>
          </div>
          <TextField
            label={LOGIN_LABELS.emailLabel}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={LOGIN_LABELS.emailPlaceholder}
            required
            fullWidth
            autoFocus
          />

          {error && (
            <p
              role="alert"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {error}
            </p>
          )}

          <SpinnerButton
            type="button"
            variant="contained"
            fullWidth
            loading={loading}
            onClick={sendResetEmail}
          >
            {LOGIN_LABELS.sendResetLink}
          </SpinnerButton>

          <Button
            variant="text"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={hideForgot}
            fullWidth
          >
            {LOGIN_LABELS.backToLogin}
          </Button>
        </div>
      );
    }

    return (
      <form onSubmit={signIn} className="flex flex-col gap-5" noValidate>
        <TextField
          label={LOGIN_LABELS.emailLabel}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={LOGIN_LABELS.emailPlaceholder}
          required
          fullWidth
        />
        <div className="flex flex-col gap-1">
          <TextField
            label={LOGIN_LABELS.passwordLabel}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={LOGIN_LABELS.passwordPlaceholder}
            required
            fullWidth
          />
          <div className="flex justify-end">
            <Button
              variant="text"
              size="small"
              onClick={showForgot}
              sx={{ fontSize: '0.75rem' }}
            >
              {LOGIN_LABELS.forgotPasswordLink}
            </Button>
          </div>
        </div>

        {error && (
          <p
            role="alert"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </p>
        )}

        <SpinnerButton type="submit" variant="contained" fullWidth loading={loading} data-testid="login-button">
          {LOGIN_LABELS.submitButton}
        </SpinnerButton>

        <div className="flex items-center gap-3 my-1">
          <hr className="flex-1 border-border" />
          <span className="text-xs text-text-disabled" aria-hidden="true">{LOGIN_LABELS.dividerText}</span>
          <hr className="flex-1 border-border" />
        </div>

        <SpinnerButton
          type="button"
          variant="outlined"
          color="inherit"
          fullWidth
          onClick={sendMagicLink}
          loading={loading}
          data-testid="magic-link-button"
        >
          {LOGIN_LABELS.magicLinkButton}
        </SpinnerButton>
      </form>
    );
  };

  return (
    <div className="relative min-h-dvh flex flex-col bg-background overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />
      </div>

      {authError && (
        <div
          role="alert"
          aria-live="assertive"
          className="relative flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border-b border-red-200"
        >
          <p className="text-sm text-red-700">{authError}</p>
          <Tooltip title={LOGIN_LABELS.dismissAlert}>
            <IconButton
              onClick={clearAuthError}
              aria-label={LOGIN_LABELS.dismissAlert}
              color="inherit"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
      )}

      <main className="relative flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <div className="mb-3 flex justify-center items-center">
              <img src="/planly-logo-light.svg" alt={APP_LABELS.appName} className="h-20 w-auto dark:hidden" />
              <img src="/planly-logo-dark.svg" alt={APP_LABELS.appName} className="h-20 w-auto hidden dark:block" />
            </div>
            <h1 className="sr-only">{APP_LABELS.appName}</h1>
          </div>

          <div className="rounded-2xl border border-border bg-surface shadow-2xl shadow-primary/10">
            <div className="p-8">
              {renderCardContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
